import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useAuth } from '@/contexts/auth-context'
import { supabase } from '@/utils/supabase/client'
import { Supplier } from '@/types'

interface SupplierFormModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: () => void
  supplier?: Supplier | null
}

export function SupplierFormModal({ isOpen, onClose, onSave, supplier }: SupplierFormModalProps) {
  const [formData, setFormData] = useState<Partial<Supplier>>({})
  const [saving, setSaving] = useState(false)
  const { currentOrganization } = useAuth()

  useEffect(() => {
    setFormData(supplier || {})
  }, [supplier])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!currentOrganization) return
    setSaving(true)

    const { id, ...data } = formData
    
    const dataToSave = {
      ...data,
      organization_id: currentOrganization.id,
    }

    let result;
    if (id) {
      result = await supabase.from('suppliers').update(dataToSave).eq('id', id)
    } else {
      result = await supabase.from('suppliers').insert(dataToSave)
    }

    const { error } = result

    if (!error) {
      onSave()
      onClose()
    } else {
      console.error(error)
    }
    setSaving(false)
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{supplier ? 'Edit Supplier' : 'Add New Supplier'}</DialogTitle>
          <DialogDescription>
            Fill in the details for the supplier.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="name" className="text-right">Name</Label>
            <Input id="name" name="name" value={formData.name || ''} onChange={handleChange} className="col-span-3" required />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="contact_person" className="text-right">Contact Person</Label>
            <Input id="contact_person" name="contact_person" value={formData.contact_person || ''} onChange={handleChange} className="col-span-3" />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="phone" className="text-right">Phone</Label>
            <Input id="phone" name="phone" value={formData.phone || ''} onChange={handleChange} className="col-span-3" />
          </div>
           <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="email" className="text-right">Email</Label>
            <Input id="email" name="email" type="email" value={formData.email || ''} onChange={handleChange} className="col-span-3" />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save Supplier'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
} 